import { useFormik } from "formik";
import { ChangeEvent, FC, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { PrivilegeContext } from "../../../components/priviledge/PriviledgeProvider";
import { UserService } from "../../../services/users/userService";
import { loadUserFromLocalStorage } from "../../../utils/jwt";
import { CompanyService } from "../../../services/companies/organizationService";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../components/bootstrap/Modal";
import FormGroup from "../../../components/bootstrap/forms/FormGroup";
import Input from "../../../components/bootstrap/forms/Input";
import Select from "../../../components/bootstrap/forms/Select";
import Button from "../../../components/bootstrap/Button";
import Spinner from "../../../components/bootstrap/Spinner";
import * as yup from 'yup';
import { SerieService } from "../../../services/series/serieService";

interface CreateModalProps {
    data?: any;
    isOpen: boolean;
    show?: boolean;
    setIsOpen(...args: unknown[]): unknown;
    onClose: (type: number, message?: string, errors?: any) => void;
}

interface SerieForm {
    name: string;
    acronym: string;
    description: string;
    company: string;
}

const validationSchema = yup.object({
    name: yup.string().required('El nombre es obligatorio'),
    acronym: yup.string().required('El acrónimo es obligatorio'),
    description: yup.string(),
    company: yup.string().required('La organización es obligatoria'),
});

const CreateModalSerie: FC<CreateModalProps> = ({ isOpen, setIsOpen, data, onClose, show }) => {

    const { userCan } = useContext(PrivilegeContext);
    const isSuperAdmin = userCan('list', 'companies');
    const serieService = new SerieService();

    const [modal, setModal] = useState(false);
    const [company, setCompany] = useState("");
    const [companyList, setCompanyList] = useState([]);

    const _handleCreateSerie = async (values: any) => {
        try {
            const response = await (await serieService.createSerie(values)).getResponseData();
            if (response.success) {
                onClose(1, response.message);
            } else {
                onClose(0, '', response.data.errors);
            }
        } catch (e) {
            toast.error('Error al crear la serie');
        }
    }

    const _handleEditSerie = async (values: any) => {
        try {
            const response = await (await serieService.editSerie(values)).getResponseData();
            if (response.success) {
                onClose(1, response.message);
            } else {
                onClose(0, '', response.data.errors);
            }
        } catch (e) {
            toast.error('Error al editar la serie');
        }
    }

    const getCompany = async () => {
        const userService = new UserService;
        const response = (await userService.getUserById(loadUserFromLocalStorage()?.id || '')).getResponseData()
        if (response.success) {
            setCompany(response.data.userRoles[0].company.id)
        }
    }

    const getCompanies = async () => {
        const companyService = new CompanyService;
        const response = (await companyService.getCompanies()).getResponseData()
        if (response.success) {
            setCompanyList(response.data.companies)
        }
    }

    const getCompanyList = () => {
        if (companyList as any) {
            return companyList.map((option: { id: any; name: any; }) => {
                return {
                    value: option.id,
                    label: option.name
                }
            })
        }
        return [];
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            series: data?.id || '',
            name: data?.name || '',
            acronym: data?.acronym || '',
            description: data?.description || '',
            company: data?.company?.id || company,
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            if (values.company != '') {
                if (data != null) {
                    _handleEditSerie(values)
                } else {
                    _handleCreateSerie(values);
                }
            }
        },
    });

    const verifyClass = (inputFieldID: keyof SerieForm) => {
        return formik.touched[inputFieldID] && formik.errors[inputFieldID] ? "is-invalid" : "";
    };

    const showErrors = (inputFieldID: keyof SerieForm) => {
        return formik.touched[inputFieldID] && formik.errors[inputFieldID] ? (<div className="invalid-feedback">{String(formik.errors[inputFieldID])}</div>) : (<></>);
    };

    useEffect(() => {
        if (!isSuperAdmin) {
            getCompany();
        } else {
            getCompanies();
        }
    }, [isSuperAdmin]);

    return (
        <Modal isOpen={isOpen} setIsOpen={setIsOpen} isStaticBackdrop={true} size='sm' titleId='Nuevo serie'>
            <ModalHeader setIsOpen={setIsOpen} className='p-4'>
                <ModalTitle id='new_code'>{show ? 'Ver serie' : data ? 'Editar serie' : 'Crear serie'}</ModalTitle>
            </ModalHeader>

            {true && !modal ? (
                <form onSubmit={formik.handleSubmit}>
                    <ModalBody className='px-4'>
                        <div className='row g-4'>
                            {isSuperAdmin && (
                                <FormGroup requiredInputLabel id='company' label='Organización' className='col-md-12'>
                                    <Select id="company" disabled={show ? true : false} list={getCompanyList()} onChange={formik.handleChange} value={formik.values.company || ''} ariaLabel='Select Companies' placeholder='Elegir Organización...' />
                                </FormGroup>
                            )}
                            <FormGroup requiredInputLabel id='name' label='Nombre:' className="col-md-12">
                                <Input value={formik.values.name} disabled={show ? true : false} autoComplete="off" onChange={(e: any) => formik.setFieldValue('name', e.target.value)} className={verifyClass('name')} />
                                {showErrors('name')}
                            </FormGroup>
                            <FormGroup requiredInputLabel id='acronym' label='Acrónimo:' className="col-md-12">
                                <Input value={formik.values.acronym} disabled={show ? true : false} autoComplete="off" onChange={(e: any) => formik.setFieldValue('acronym', e.target.value)} className={verifyClass('acronym')} />
                                {showErrors('acronym')}
                            </FormGroup>
                            <FormGroup id='description' label='Descripción:' className="col-md-12">
                                <textarea
                                    id="description"
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => formik.setFieldValue('description', e.target.value)}
                                    rows={3}
                                    value={formik.values.description}
                                    autoComplete="off"
                                    disabled={show ? true : false}
                                    className={verifyClass('description')}
                                    style={{
                                        borderRadius: '10px',
                                        boxShadow: show ? 'none' : '0 0 0 3px rgb(249, 230, 236)',
                                        backgroundColor: show ? '#e9ecef' : '#f8f9fa',
                                        width: '100%',
                                        padding: '15px',
                                        border: '0'
                                    }}
                                />
                                {showErrors('description')}
                            </FormGroup>
                        </div>
                    </ModalBody>

                    <ModalFooter className='px-4 pb-4 d-flex'>
                        <Button color='secondary' type="submit" icon="Save" className={show ? 'd-none' : 'd-block'}>
                            {data ? 'Editar' : 'Crear'}
                        </Button>
                    </ModalFooter>
                </form>

            ) : (
                <div className="d-flex justify-content-center p-5">
                    <Spinner />
                </div>
            )}
        </Modal>
    )
}

export default CreateModalSerie;